import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {
    addFavoritePerformer,
    addFavoriteVenue,
    fetchAccessToken,
    fetchFavoritePerformers,
    fetchFavoriteVenues,
    removeFavoritePerformer,
    removeFavoriteVenue,
} from '../api/endpoints';
import UserDto from '../interfaces/UserDto';
import LoginResponse from '../interfaces/LoginResponse';
import FavoriteDto from '../interfaces/FavoriteDto';
import {isRight} from "fp-ts/Either";


interface AuthenticationContextType {
    accessToken: string | null;
    expiry: number | null;
    user: UserDto | null;
    isLoggedIn: boolean;
    isAuthLoading: boolean;
    setLogin: (loginResponse: LoginResponse | null) => void;

    favoritePerformers: FavoriteDto[];
    favoriteVenues: FavoriteDto[];
    favoritePerformerIds: Set<number>;
    favoriteVenueIds: Set<number>;
    isPerformerFavorited: (tevoId: number) => boolean;
    isVenueFavorited: (tevoId: number) => boolean;
    toggleFavoritePerformer: (dto: FavoriteDto) => Promise<void>;
    toggleFavoriteVenue: (dto: FavoriteDto) => Promise<void>;
    refreshFavorites: () => Promise<void>;
}


const AuthenticationContext = createContext<AuthenticationContextType>({
    accessToken: null,
    expiry: null,
    user: null,
    isLoggedIn: false,
    isAuthLoading: true,
    setLogin: () => {
    },
    favoritePerformers: [],
    favoriteVenues: [],
    favoritePerformerIds: new Set(),
    favoriteVenueIds: new Set(),
    isPerformerFavorited: () => false,
    isVenueFavorited: () => false,
    toggleFavoritePerformer: async () => {
    },
    toggleFavoriteVenue: async () => {
    },
    refreshFavorites: async () => {
    },
});

export const useAuthenticationContext = () => useContext(AuthenticationContext);

interface AuthenticationProviderProps {
    children: React.ReactNode;
}

export const AuthenticationProvider: React.FC<AuthenticationProviderProps> = ({children}) => {

    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [expiry, setExpiry] = useState<number | null>(null);
    const [user, setUser] = useState<UserDto | null>(null);

    const [favoritePerformers, setFavoritePerformers] = useState<FavoriteDto[]>([]);
    const [favoriteVenues, setFavoriteVenues] = useState<FavoriteDto[]>([]);


    const updateToken = useCallback(async () => {
        setIsAuthLoading(true);
        try {
            const loginResponseEither = await fetchAccessToken();
            if (isRight(loginResponseEither)) {
                const loginResponse = loginResponseEither.right;
                setAccessToken(loginResponse.token);
                setExpiry(loginResponse.expiry);
                setUser(loginResponse.user);
                setLoginFromTokenAndExpiry(loginResponse.token, loginResponse.expiry)
            } else {
                console.error(loginResponseEither.left)
            }

        } catch (error) {
            console.error('Failed to fetch access token', error);
            setIsLoggedIn(false);
        } finally {
            setIsAuthLoading(false);
        }
    }, []);


    useEffect(() => {
        updateToken();

        if (expiry != null) {
            const interval = setInterval(() => {
                updateToken();
            }, expiry - Date.now());

            return () => clearInterval(interval);
        }

    }, [updateToken]);

    const setLoginFromTokenAndExpiry = (accessToken: string | null, expiry: number | null) => {
        if (accessToken && expiry) {
            const now = new Date().getTime();
            setIsLoggedIn(expiry > now);
        } else {
            setIsLoggedIn(false);
        }
    }

    const setLogin = (login: LoginResponse | null) => {
        if (login == null) {
            setAccessToken(null);
            setExpiry(null);
            setUser(null);
            setLoginFromTokenAndExpiry(null, null)
        } else {
            setAccessToken(login.token);
            setExpiry(login.expiry);
            setUser(login.user);
            setLoginFromTokenAndExpiry(login.token, login.expiry)
        }
    }

    const refreshFavorites = useCallback(async () => {
        const [performersEither, venuesEither] = await Promise.all([
            fetchFavoritePerformers(),
            fetchFavoriteVenues(),
        ]);
        if (isRight(performersEither)) {
            setFavoritePerformers(performersEither.right);
        } else {
            console.error(performersEither.left);
        }
        if (isRight(venuesEither)) {
            setFavoriteVenues(venuesEither.right);
        } else {
            console.error(venuesEither.left);
        }
    }, []);

    useEffect(() => {
        if (isLoggedIn) {
            refreshFavorites();
        } else {
            setFavoritePerformers([]);
            setFavoriteVenues([]);
        }
    }, [isLoggedIn, refreshFavorites]);

    const favoritePerformerIds = useMemo(
        () => new Set(favoritePerformers.map(p => p.tevoId)),
        [favoritePerformers]
    );
    const favoriteVenueIds = useMemo(
        () => new Set(favoriteVenues.map(v => v.tevoId)),
        [favoriteVenues]
    );

    const isPerformerFavorited = useCallback(
        (tevoId: number) => favoritePerformerIds.has(tevoId),
        [favoritePerformerIds]
    );
    const isVenueFavorited = useCallback(
        (tevoId: number) => favoriteVenueIds.has(tevoId),
        [favoriteVenueIds]
    );

    const toggleFavoritePerformer = useCallback(async (dto: FavoriteDto) => {
        let alreadyFavorited = false;
        let prev: FavoriteDto[] = [];
        setFavoritePerformers(current => {
            prev = current;
            alreadyFavorited = current.some(p => p.tevoId === dto.tevoId);
            if (alreadyFavorited) {
                return current.filter(p => p.tevoId !== dto.tevoId);
            }
            return [...current.filter(p => p.tevoId !== dto.tevoId), {tevoId: dto.tevoId, name: dto.name}];
        });

        if (alreadyFavorited) {
            const result = await removeFavoritePerformer(dto.tevoId);
            if (!isRight(result)) {
                console.error(result.left);
                setFavoritePerformers(prev);
            }
        } else {
            const result = await addFavoritePerformer(dto);
            if (isRight(result)) {
                const saved = result.right;
                if (saved && typeof saved === 'object' && typeof (saved as FavoriteDto).tevoId === 'number') {
                    setFavoritePerformers(current => [
                        ...current.filter(p => p.tevoId !== dto.tevoId),
                        saved,
                    ]);
                }
            } else {
                console.error(result.left);
                setFavoritePerformers(prev);
            }
        }
    }, []);

    const toggleFavoriteVenue = useCallback(async (dto: FavoriteDto) => {
        let alreadyFavorited = false;
        let prev: FavoriteDto[] = [];
        setFavoriteVenues(current => {
            prev = current;
            alreadyFavorited = current.some(v => v.tevoId === dto.tevoId);
            if (alreadyFavorited) {
                return current.filter(v => v.tevoId !== dto.tevoId);
            }
            return [...current.filter(v => v.tevoId !== dto.tevoId), {tevoId: dto.tevoId, name: dto.name}];
        });

        if (alreadyFavorited) {
            const result = await removeFavoriteVenue(dto.tevoId);
            if (!isRight(result)) {
                console.error(result.left);
                setFavoriteVenues(prev);
            }
        } else {
            const result = await addFavoriteVenue(dto);
            if (isRight(result)) {
                const saved = result.right;
                if (saved && typeof saved === 'object' && typeof (saved as FavoriteDto).tevoId === 'number') {
                    setFavoriteVenues(current => [
                        ...current.filter(v => v.tevoId !== dto.tevoId),
                        saved,
                    ]);
                }
            } else {
                console.error(result.left);
                setFavoriteVenues(prev);
            }
        }
    }, []);

    return (
        <AuthenticationContext.Provider value={{
            accessToken, expiry, user, isLoggedIn, isAuthLoading, setLogin,
            favoritePerformers, favoriteVenues, favoritePerformerIds, favoriteVenueIds,
            isPerformerFavorited, isVenueFavorited,
            toggleFavoritePerformer, toggleFavoriteVenue, refreshFavorites,
        }}>
            {children}
        </AuthenticationContext.Provider>
    );
};

export default AuthenticationProvider;
